import { format, parseISO } from 'date-fns';

/**
 * Format a date for display in a consistent, user-friendly format
 * @param {Date|string|number} dateInput - The date to format
 * @param {string} formatStr - The format string to use (default: 'EEEE, MMMM d, yyyy')
 * @returns {string} The formatted date string
 */
export const formatDate = (dateInput, formatStr = 'EEEE, MMMM d, yyyy') => {
  try {
    if (!dateInput) return '';
    
    let date;
    
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      // Check if it's a numeric timestamp as string
      if (/^\d+$/.test(dateInput)) {
        date = new Date(parseInt(dateInput, 10));
      } else {
        // Try parsing as ISO string
        date = parseISO(dateInput);
      }
    } else if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else {
      console.error('Invalid date type in formatDate:', typeof dateInput);
      return '';
    }
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date in formatDate:', dateInput);
      return '';
    }
    
    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return '';
  }
};

/**
 * Format a date as YYYY-MM-DD for consistent date keys
 * @param {Date|string|number} date - The date to format
 * @returns {string|null} The formatted date string or null if invalid
 */
export const formatDateKey = (date) => {
  try {
    // Handle different input types
    let dateObj;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // Check if it's a timestamp in string format (all digits)
      if (/^\d+$/.test(date)) {
        dateObj = new Date(parseInt(date, 10));
        console.log(`Parsed timestamp string ${date} into date: ${dateObj.toISOString()}`);
      }
      // Special case for SQL date format (YYYY-MM-DD)
      else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date; // Already in correct format
      }
      else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      dateObj = new Date(date); // Timestamp
      console.log(`Parsed timestamp number ${date} into date: ${dateObj.toISOString()}`);
    } else {
      console.error('Invalid date type:', typeof date, date);
      return null;
    }
  
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date object:', date);
      return null;
    }
    
    // Format as YYYY-MM-DD without timezone issues by using local date components
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formatted = `${year}-${month}-${day}`;
    
    return formatted;
  } catch (error) {
    console.error('Error in formatDateKey:', error, 'for date:', date);
    return null;
  }
};

/**
 * Format a date to the start of day in ISO format
 * @param {Date|string} date - Date to format
 * @returns {string} - ISO string representing start of day
 */
export const formatStartOfDay = (date) => {
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date for start of day:', date);
      return null;
    }
    
    // Set to start of day in local timezone
    dateObj.setHours(0, 0, 0, 0);
    return dateObj.toISOString();
  } catch (error) {
    console.error('Error formatting start of day:', error);
    return null;
  }
};

/**
 * Format a date to the end of day in ISO format
 * @param {Date|string} date - Date to format
 * @returns {string} - ISO string representing end of day
 */
export const formatEndOfDay = (date) => {
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date for end of day:', date);
      return null;
    }
    
    // Set to end of day in local timezone
    dateObj.setHours(23, 59, 59, 999);
    return dateObj.toISOString();
  } catch (error) {
    console.error('Error formatting end of day:', error);
    return null;
  }
};

/**
 * Get the formatted date range for display
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {string} - Formatted date range
 */
export const getFormattedDateRange = (startDate, endDate) => {
  if (!startDate) return '';
  
  const start = formatDate(startDate, 'MMM d, yyyy');
  
  if (!endDate || startDate === endDate) {
    return start;
  }
  
  const end = formatDate(endDate, 'MMM d, yyyy');
  return `${start} - ${end}`;
};

/**
 * Format a due date for display without timezone issues
 * @param {string|Date} dueDate - The due date to format
 * @param {string} formatStr - The format string to use (default: 'MMM d, yyyy')
 * @returns {string} The formatted date string
 */
export const formatDueDate = (dueDate, formatStr = 'MMM d, yyyy') => {
  try {
    if (!dueDate) return '';
    
    let date;
    
    if (typeof dueDate === 'number') {
      // Handle numeric timestamps (milliseconds since epoch)
      date = new Date(dueDate);
    } else if (typeof dueDate === 'string') {
      // If it's already a YYYY-MM-DD string, parse it as local date to avoid timezone shift
      if (dueDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dueDate.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else if (dueDate.includes('T')) {
        // If it's an ISO string, parse it but use local date components
        const isoDate = new Date(dueDate);
        date = new Date(isoDate.getFullYear(), isoDate.getMonth(), isoDate.getDate());
      } else {
        date = new Date(dueDate);
      }
    } else if (dueDate instanceof Date) {
      // Use local date components to avoid timezone issues
      date = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    } else {
      return '';
    }
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting due date:', error, dueDate);
    return '';
  }
}; 